import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import apiClient from "@/lib/api";
import PropertyCard from "@/components/property/PropertyCard";
import { photoSrc } from "@/lib/photos";
import { resolveCardPrice } from "@/components/property/roomMeta";
import { PROPERTY_TYPE_LABELS } from "@/components/property/PropertyStatusBadge";

const cardOf = (x) => {
  const priced = resolveCardPrice(x, "month");
  return {
    type: PROPERTY_TYPE_LABELS[x.property_type] || x.property_type,
    name: x.name,
    location: `${x.location?.district_name || ""}, ${x.location?.city_name || ""}`.replace(/^, /, ""),
    rating: x.rating || 0,
    reviewCount: x.review_count || 0,
    amenities: (x.amenities || []).map((a) => a.name),
    photo: x.cover_photo_id ? photoSrc(x.cover_photo_id) : null,
    price: priced.label,
    priceSuffix: priced.suffix,
  };
};

export default function FeaturedSection() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const trackRef = useRef(null);
  const hoverRef = useRef(false);

  useEffect(() => {
    apiClient.get("/properties/featured", { params: { limit: 6 } })
      .then((r) => setItems(r.data?.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  // Repeat items enough to overflow the viewport, then duplicate once for a seamless loop.
  const loopItems = useMemo(() => {
    if (items.length === 0) return [];
    let base = [...items];
    while (base.length < 5) base = base.concat(items);
    return base.concat(base);
  }, [items]);

  // Continuous auto-scroll to the right (marquee), paused on hover/touch. Loops seamlessly.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || loopItems.length === 0) return;
    let raf;
    const speed = 0.6; // px per frame
    const tick = () => {
      if (el && !hoverRef.current) {
        el.scrollLeft += speed;
        const half = el.scrollWidth / 2;
        if (half > 0 && el.scrollLeft >= half) el.scrollLeft -= half;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [loopItems]);

  // hidden entirely when there are no featured properties
  if (!loaded || items.length === 0) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20" data-testid="featured-section">
      <div className="mb-8">
        <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.2em] text-accent">
          <Star size={14} className="fill-accent" /> Pilihan AVASA
        </span>
        <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
          Rekomendasi Hunian Untukmu
        </h2>
        <p className="mt-2 text-base text-muted-foreground max-w-2xl">
          Temukan hunian pilihan yang sedang dipromosikan di AVASA.
        </p>
      </div>

      <div
        ref={trackRef}
        onMouseEnter={() => { hoverRef.current = true; }}
        onMouseLeave={() => { hoverRef.current = false; }}
        onTouchStart={() => { hoverRef.current = true; }}
        onTouchEnd={() => { hoverRef.current = false; }}
        className="flex gap-4 sm:gap-5 overflow-x-auto pb-4 -mx-1 px-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        data-testid="featured-track"
      >
        {loopItems.map((x, i) => (
          <motion.div
            key={`${x.id}-${i}`}
            data-featured-card
            data-testid={`featured-card-${i}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i, 4) * 0.06, duration: 0.4 }}
            onClick={() => navigate(`/property/${x.id}`)}
            className="relative shrink-0 w-[56%] sm:w-[46%] lg:w-[31.5%] cursor-pointer rounded-2xl ring-2 ring-accent/60 ring-offset-2 ring-offset-background"
          >
            <span className="absolute top-10 left-2 sm:top-12 sm:left-3 z-10 inline-flex items-center gap-1 text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full bg-accent text-accent-foreground shadow-md">
              <Star size={10} className="fill-current" /> Featured
            </span>
            <PropertyCard index={i} property={cardOf(x)} propertyId={x.id} compact />
          </motion.div>
        ))}
      </div>
    </section>
  );
}
